import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { COLOR } from '@dized/ui';

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const ToggleButton = styled.div`
  width: 22px;
  height: 22px;
  padding: 4px;
  border-radius: 4px;
  background-color: ${props => props.disabled ? COLOR.DISABLED_GRAY : COLOR.TURQUOISE};
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  .material-icons {
    color: ${COLOR.LIGHT_GRAY};
    font-size: 16px;
    line-height: 1;
    user-select: none;
  }
`;

const DropdownContainer = styled.div`
  position: fixed;
  z-index: 9999;
  min-width: 180px;
  max-width: 280px;
  max-height: 300px;
  background: white;
  border: 2px solid #c2c2c3;
  border-radius: 4px;
  box-shadow: #cacab7 0px 0px 10px 1px;
  overflow: hidden;
  margin-top: 2px;
  display: flex;
  flex-direction: column;
`;

const DropdownHeader = styled.div`
  padding: 8px 12px;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  background: #f5f5f5;
  border-bottom: 1px solid rgba(194, 194, 195, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid rgba(194, 194, 195, 0.5);
  font-family: Roboto, sans-serif;
  font-size: 14px;
  outline: none;
  
  &::placeholder {
    color: #999;
    font-style: italic;
  }
`;

const DropdownList = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 200px;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const DropdownItem = styled.div`
  padding: 6px 12px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 1.3;
  cursor: pointer;
  background: white;
  border-bottom: solid 1px rgba(194, 194, 195, 0.3);
  transition: background 0.2s;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &:hover {
    background: ${COLOR.LIGHT_GRAY};
  }

  &:last-child {
    border-bottom: none;
  }

  &.disabled {
    color: #ababab;
    background-color: #ebebeb0a;
    cursor: default;
  }
  
  &.no-results {
    color: #999;
    font-style: italic;
    cursor: default;
    text-align: center;
  }
`;

/**
 * Custom dropdown menu component with proper positioning
 * @param {Object} props - Component props
 * @returns {React.Component} The CustomDropdown component
 */
export const DropdownPicker = ({ 
  options = [], 
  onChange, 
  disabled = false,
  title = null,
  searchPlaceholder = 'Search...',
  searchable = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  
  // Generate unique ID for this instance
  const instanceId = useRef(`dropdown-${Math.random().toString(36).substr(2, 9)}`).current;

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm.trim()) {
      return options;
    }
    return options.filter(option => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm, searchable]);

  const calculateDropdownPosition = () => {
    if (!buttonRef.current) return { top: 0, left: 0 };
    
    const rect = buttonRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    
    let top = rect.bottom + window.scrollY;
    let left = rect.left + window.scrollX;
    
    // Check if dropdown would go off the bottom of the screen
    const dropdownHeight = 300; // max height of dropdown
    if (rect.bottom + dropdownHeight > windowHeight) {
      top = rect.top + window.scrollY - dropdownHeight;
    }
    
    // Check if dropdown would go off the right side of the screen
    const dropdownWidth = 280; // max width of dropdown
    if (rect.left + dropdownWidth > windowWidth) {
      // Position dropdown to the left of the button instead of centering
      left = rect.right + window.scrollX - dropdownWidth;
      // Ensure it doesn't go off the left side
      if (left < 10) {
        left = 10;
      }
    } else {
      // Center the dropdown relative to the button for better positioning
      const buttonWidth = rect.width;
      left = rect.left + window.scrollX + (buttonWidth / 2) - (dropdownWidth / 2);
      
      // Ensure it doesn't go off the left side
      if (left < 10) {
        left = 10;
      }
      // Ensure it doesn't go off the right side
      if (left + dropdownWidth > windowWidth - 10) {
        left = windowWidth - dropdownWidth - 10;
      }
    }
    
    return { top, left };
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm(''); // Reset search when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm(''); // Reset search when opening
      }
    }
  };

  const handleItemClick = (option) => {
    setIsOpen(false);
    setSearchTerm('');
    if (onChange) {
      onChange(option.value);
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <ToggleButton 
        ref={buttonRef} 
        disabled={disabled} 
        onClick={handleToggleClick}
        role="button"
        aria-label="Add language"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggleClick(e);
          }
        }}
      >
        <i className="material-icons">add</i>
      </ToggleButton>
      {isOpen && !disabled && (
        <DropdownContainer 
          style={calculateDropdownPosition()}
          role="listbox"
          aria-label={title || 'Language selection'}
        >
          {title && <DropdownHeader>{title}</DropdownHeader>}
          {searchable && (
            <SearchInput
              id={`${instanceId}-search-input`}
              name={`${instanceId}-search`}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              aria-label="Search languages"
            />
          )}
          <DropdownList role="list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <DropdownItem
                  key={option.value || index}
                  className={option.disabled ? 'disabled' : ''}
                  onClick={() => !option.disabled && handleItemClick(option)}
                  role="option"
                  aria-selected="false"
                  tabIndex={option.disabled ? -1 : 0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (!option.disabled) handleItemClick(option);
                    }
                  }}
                >
                  {option.label}
                </DropdownItem>
              ))
            ) : (
              <DropdownItem className="no-results" role="option">
                No options found
              </DropdownItem>
            )}
          </DropdownList>
        </DropdownContainer>
      )}
    </Wrapper>
  );
};

DropdownPicker.propTypes = {
  /** Array of options with { label, value, disabled } */
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
    disabled: PropTypes.bool
  })),
  /** Callback when selection changes */
  onChange: PropTypes.func,
  /** Whether the dropdown is disabled */
  disabled: PropTypes.bool,
  /** Optional title header for the dropdown */
  title: PropTypes.string,
  /** Placeholder for search input */
  searchPlaceholder: PropTypes.string,
  /** Whether to show search input */
  searchable: PropTypes.bool
};

DropdownPicker.defaultProps = {
  options: [],
  disabled: false,
  title: null,
  searchPlaceholder: 'Search...',
  searchable: true
};
